import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import { UserList } from "../users/UserList";
import { ProfileSettings } from "./ProfileSettings";
import { KioskDevices } from "./KioskDevices";
import { ReviewerSettings } from "./ReviewerSettings";

// 設定頁分成三個分頁:
//
// - **公司資料**:業主自身識別資料。報價單 PDF 的抬頭、統編、匯款資訊全部從
//   這裡讀,不寫死在程式或版型裡(規格 §2.4)。**員工與財務看得到、改不動**
//   (業主 2026-09-25)—— 欄位唯讀、沒有儲存鈕,不是按下去才說不行。
// - **個人資料**:每個人自己的姓名、電話、Email;角色與年度特休只能看。
// - **使用者**:開帳號、停用同事、代設密碼。**只有老闆看得到,員工連分頁都沒有。**
//
// 報價單列表的欄位設定**曾經是第三個分頁,已經搬走**(業主 2026-09-13:
// 「再設定裡面的列表欄位顯示 搬到報價單列表頁面 不要單獨去設定頁面 會不清楚」)。
// 現在的入口是列表頁右上那顆「顯示欄位」,而且**只有那一個** ——
// 同一件事留兩個入口比放錯地方更糟:兩邊各存各的,使用者不知道誰說了算。
// 2026-09-25 起每個人一份,存在自己的帳號上(`/api/users/me/list-columns`)。
//
// 統一發票專用章與 logo 的檔案路徑**刻意不在這裡** —— 印章存在容器私有目錄,
// 連它的路徑都沒必要讓瀏覽器知道。
//
// 分頁走網址(/settings、/settings/profile 與 /settings/users)而不是元件內的 state:
// 老闆要能把「使用者管理」那一頁的連結直接存成書籤,重新整理也還在同一個分頁。

interface CompanySettingsForm {
  company_name_zh: string;
  company_name_en: string;
  tax_id: string;
  address: string;
  phone: string;
  fax: string;
  email: string;
  bank_name: string;
  bank_branch: string;
  bank_account: string;
  bank_account_name: string;
}

const EMPTY: CompanySettingsForm = {
  company_name_zh: "",
  company_name_en: "",
  tax_id: "",
  address: "",
  phone: "",
  fax: "",
  email: "",
  bank_name: "",
  bank_branch: "",
  bank_account: "",
  bank_account_name: "",
};

const FIELDS: Array<{ key: keyof CompanySettingsForm; label: string; hint?: string }> = [
  { key: "company_name_zh", label: "公司名稱(中文)" },
  { key: "company_name_en", label: "公司名稱(英文)" },
  { key: "tax_id", label: "統一編號" },
  { key: "address", label: "地址" },
  { key: "phone", label: "電話", hint: "會印在報價單抬頭" },
  { key: "fax", label: "傳真" },
  { key: "email", label: "Email" },
  { key: "bank_name", label: "銀行" },
  { key: "bank_branch", label: "分行" },
  { key: "bank_account", label: "帳號" },
  { key: "bank_account_name", label: "戶名" },
];

type SettingsTab = "company" | "profile" | "users" | "kiosks" | "reviewers";

const USERS_PATH = "/settings/users";
const PROFILE_PATH = "/settings/profile";
const KIOSKS_PATH = "/settings/kiosks";
const REVIEWERS_PATH = "/settings/reviewers";

// 每個分頁自己的寬度上限。
//
// 業主 2026-09-13:「設定頁面 寬度不夠 使用者內的欄位資料都被壓縮 很醜」——
// 原本每個分頁都共用一個 max-w-3xl(48rem),而使用者那張表有七欄
// (姓名／Email／角色／狀態／年度特休／建立日期／動作),擠進 48rem 就糊成一團。
//
// 但**不能一起放寬**:公司資料是一欄到底的表單,行長拉到 72rem 每一行都讀得很累。
// 表單要窄、表格要寬,這是兩種不同的需求,不該共用同一個數字。
// tests/settings-width.test.tsx 守著「不得又變回同一個值」。
const MAX_WIDTH: Record<SettingsTab, string> = {
  company: "max-w-3xl",
  profile: "max-w-3xl",
  kiosks: "max-w-3xl",
  reviewers: "max-w-3xl",
  users: "max-w-6xl",
};

/** 網址決定分頁。結尾的斜線不影響判斷。 */
function tabOf(pathname: string): SettingsTab {
  const path = pathname.replace(/\/+$/, "");
  if (path.endsWith("/users")) return "users";
  if (path.endsWith("/profile")) return "profile";
  if (path.endsWith("/kiosks")) return "kiosks";
  if (path.endsWith("/reviewers")) return "reviewers";
  return "company";
}

/** 老闆專屬的分頁。員工自己打網址進來也只會看到這一句。 */
function OwnerOnly({ what }: { what: string }) {
  // 前端擋只是不讓人白跑一趟,真正說了算的是後端的 403。
  return (
    <p role="alert" className="m-0 text-sm text-[var(--danger)]">
      {what}只有老闆能使用。
    </p>
  );
}

export function SettingsPage() {
  const location = useLocation();
  const { user } = useAuth();
  const tab = tabOf(location.pathname);
  const isOwner = user?.role === "OWNER";

  return (
    <div className={`mx-auto flex w-full flex-col gap-4 ${MAX_WIDTH[tab]}`}>
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">設定</h2>

      <nav aria-label="設定分頁" className="flex gap-1 border-b border-[var(--border)]">
        <TabLink to="/settings" label="公司資料" current={tab === "company"} />
        <TabLink to={PROFILE_PATH} label="個人資料" current={tab === "profile"} />
        {/* 員工連這個分頁都看不到。看得到卻按不動,只會讓人一直來問為什麼。 */}
        {isOwner ? <TabLink to={USERS_PATH} label="使用者" current={tab === "users"} /> : null}
        {/* 門口打卡機(業主 2026-09-25)。老闆專屬。 */}
        {isOwner ? <TabLink to={KIOSKS_PATH} label="打卡機" current={tab === "kiosks"} /> : null}
        {/* 覆核指派(業主 2026-11-02)。老闆專屬。 */}
        {isOwner ? <TabLink to={REVIEWERS_PATH} label="覆核指派" current={tab === "reviewers"} /> : null}
      </nav>

      {tab === "users" ? (
        isOwner ? <UserList currentUserId={user?.id ?? null} /> : <OwnerOnly what="使用者管理" />
      ) : tab === "kiosks" ? (
        isOwner ? <KioskDevices /> : <OwnerOnly what="打卡機管理" />
      ) : tab === "reviewers" ? (
        isOwner ? <ReviewerSettings /> : <OwnerOnly what="覆核指派" />
      ) : tab === "profile" ? (
        <ProfileSettings />
      ) : (
        <CompanySettings editable={isOwner} />
      )}
    </div>
  );
}

interface TabLinkProps {
  to: string;
  label: string;
  current: boolean;
}

/** 分頁連結。目前所在的那一頁用 aria-current 標出來,不只靠顏色。 */
function TabLink({ to, label, current }: TabLinkProps) {
  return (
    <Link
      to={to}
      aria-current={current ? "page" : undefined}
      className={
        "-mb-px border-b-2 px-3 py-2 text-sm font-medium " +
        (current
          ? "border-[var(--accent)] text-[var(--text)]"
          : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]")
      }
    >
      {label}
    </Link>
  );
}

function CompanySettings({ editable }: { editable: boolean }) {
  const toast = useToast();
  const [form, setForm] = useState<CompanySettingsForm>(EMPTY);

  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<Partial<CompanySettingsForm>>("/settings"),
  });

  // 只取這一頁管得到的欄位。`GET /api/settings` 回來的東西日後若多了別的設定,
  // 整包倒進表單再原封送回去,等於這一頁每存一次檔就順手覆寫別處的設定。
  useEffect(() => {
    if (!data) return;
    const next = { ...EMPTY };
    for (const field of FIELDS) {
      const value = data[field.key];
      if (typeof value === "string") next[field.key] = value;
    }
    setForm(next);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put<CompanySettingsForm>("/settings", form),
    onSuccess: () => toast.push("已儲存", "success"),
    onError: (err) => toast.push(err instanceof Error ? err.message : "儲存失敗", "error"),
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (editable) save.mutate();
      }}
      className="flex w-full flex-col gap-4"
    >
      <p className="m-0 text-sm text-[var(--text-2)]">
        這些資料會直接出現在報價單 PDF 的抬頭。{editable ? "只有業主改得動。" : "只有老闆可以修改。"}
      </p>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          設定載入失敗,請稍後再試。
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {FIELDS.map((field) => (
          <Input
            key={field.key}
            label={field.label}
            hint={field.hint}
            name={field.key}
            readOnly={!editable}
            value={form[field.key]}
            onChange={(event) => setForm((prev) => ({ ...prev, [field.key]: event.target.value }))}
          />
        ))}
      </div>

      {editable ? (
        <div>
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            icon={<Save aria-hidden="true" size={16} />}
          >
            儲存設定
          </Button>
        </div>
      ) : null}
    </form>
  );
}
